/* eslint-disable class-methods-use-this */
class ShAuth {
	constructor(accountUrl, clientId) {
		// cookies names
		this.cUserId = "sh_UId";
		this.cToken = "sh_Tk";
		this.cRefresh = "sh_RT";
		this.cIdToken = "sh_ITk";
		this.cReturnUrl = "sh_RU";
		// endpoints
		this.authorizeUrl = `${accountUrl}/authentication/oauth/authorize`;
		this.tokenUrl = `${accountUrl}/authentication/oauth/token`;
		this.logoutUrl = `${accountUrl}/authentication/logout`;
		this.redirectUri = window.location.origin;
		// Shopify customer api credential
		this.clientId = clientId;
		this.accountUrl = accountUrl;
		this.loadingEl = document.getElementById("loading-state");
		this.loadingMessageEl = this.loadingEl.querySelector("p");
	}

	setCookie(name, value, expireIn) {
		let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; Secure; SameSite=Strict`;

		if (expireIn) {
			const expiryDate = new Date();
			expiryDate.setTime(expiryDate.getTime() + expireIn);
			cookie += `; expires=${expiryDate.toUTCString()}`;
		}

		document.cookie = cookie;
	}

	getCookie(name) {
		return document.cookie
			.split("; ")
			.find((row) => row.startsWith(`${name}=`))
			?.split("=")[1];
	}

	decodeToken(token) {
		const cleaned = token.startsWith("shcat_") ? token.slice(6) : token;
		const parts = cleaned.split(".");

		if (parts.length !== 3) throw new Error("Invalid ID token");

		const payload = parts[1];
		const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
		return JSON.parse(json);
	}

	checkUrlParams(...search) {
		const url = new URL(document.location);
		const { searchParams } = url;

		if (searchParams.size === 0) return false;

		const found = [];
		search.forEach((p) => {
			if (searchParams.get(p)) {
				found.push({ name: p, value: searchParams.get(p) });
				searchParams.delete(p);
			}
		});

		if (found.length === 0) return false;

		const params = Object.fromEntries(found.map((p) => [p.name, p.value]));

		window.history.replaceState(null, "", url);
		return params;
	}

	clearSession() {
		this.setCookie(this.cToken, "", -1);
		this.setCookie(this.cIdToken, "", -1);
		this.setCookie(this.cUserId, "", -1);
		this.setCookie(this.cRefresh, "", -1);
	}

	generateCodeVerifier() {
		const array = new Uint8Array(32);
		crypto.getRandomValues(array);
		// Base64url encode
		return btoa(String.fromCharCode.apply(null, array)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
	}

	async updateAccountLinks(userId) {
		const accountlinksEl = document.getElementById("account-links");
		const accountToggle = document.getElementById("account-toggle");

		if (!userId) {
			// update login link
			const loginLink = await this.generateAuthLink();
			accountToggle.addEventListener("click", () => {
				window.location.href = loginLink;
			});
		} else {
			// enable toggle
			accountToggle.addEventListener("click", () => {
				accountToggle.toggleAttribute("data-logged");
			});

			// add events to logout btn
			const logoutBtnEl = accountlinksEl.querySelector("a.logout");
			logoutBtnEl.addEventListener("click", this.execLogout.bind(this));
			document.addEventListener("click", (e) => {
				if (!accountlinksEl.contains(e.target) && !accountToggle.contains(e.target)) {
					accountToggle.toggleAttribute("data-logged", false);
				}
			});
		}
	}

	async generateCodeChallenge(verifier) {
		const encoder = new TextEncoder();
		const data = encoder.encode(verifier);
		const digest = await crypto.subtle.digest("SHA-256", data);
		const hash = new Uint8Array(digest);

		let str = "";
		// eslint-disable-next-line no-return-assign
		hash.forEach((b) => (str += String.fromCharCode(b)));

		return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
	}

	async generateAuthLink() {
		const verifier = this.generateCodeVerifier();
		const challenge = await this.generateCodeChallenge(verifier);
		const state = crypto.randomUUID();

		localStorage.setItem("pkce_verifier", verifier);
		localStorage.setItem("pkce_state", state);

		const authorizationRequestUrl = new URL(this.authorizeUrl);

		authorizationRequestUrl.searchParams.append("scope", "openid");
		authorizationRequestUrl.searchParams.append("client_id", this.clientId);
		authorizationRequestUrl.searchParams.append("response_type", "code");
		authorizationRequestUrl.searchParams.append("redirect_uri", this.redirectUri);
		authorizationRequestUrl.searchParams.append("state", state);
		authorizationRequestUrl.searchParams.append("code_challenge", challenge);
		authorizationRequestUrl.searchParams.append("code_challenge_method", "S256");

		return authorizationRequestUrl.toString();
	}

	async exchangeToken({ state, code, refreshToken }) {
		const body = new URLSearchParams();
		// Keep copy from id_token (needed to logout) and refresh doesn't include it
		const idTokenCopy = this.getCookie(this.cIdToken);

		if (refreshToken) {
			body.append("grant_type", "refresh_token");
			body.append("client_id", this.clientId);
			body.append("refresh_token", refreshToken);
		} else {
			const verifier = localStorage.getItem("pkce_verifier");
			const compareState = localStorage.getItem("pkce_state");
			if (state !== compareState) return null;

			body.append("grant_type", "authorization_code");
			body.append("redirect_uri", this.redirectUri);
			body.append("client_id", this.clientId);
			body.append("code_verifier", verifier);
			body.append("code", code);
		}

		const response = await fetch(this.tokenUrl, {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body,
		});

		if (response.status !== 200) {
			// clear all session cookies
			this.clearSession();
			return null;
		}

		const data = await response.json();

		const { id_token: idToken, access_token: token, expires_in: expIn, refresh_token: refToken } = data;
		const { sub: userId } = this.decodeToken(token);
		// we don't know how long the refresh token is valid and are assuming 7 days.
		const refreshExpIn = 7 * 24 * 60 * 60 * 1000;

		this.setCookie(this.cToken, token, expIn * 1000); // 1h
		this.setCookie(this.cIdToken, idToken || idTokenCopy, refreshExpIn);
		this.setCookie(this.cUserId, userId, refreshExpIn);
		this.setCookie(this.cRefresh, refToken, refreshExpIn);

		return userId;
	}

	async execLogout(e) {
		e.preventDefault();
		const idToken = this.getCookie(this.cIdToken);
		this.clearSession();
		this.loadingEl.classList.remove("hidden");
		this.loadingMessageEl.innerText = "Logging you out";
		window.location.href = `${this.logoutUrl}?id_token_hint=${idToken}&post_logout_redirect_uri=${this.redirectUri}`;
	}

	redirect(dest) {
		const destDecoded = decodeURIComponent(dest);
		const { hostname, protocol } = window.location;

		window.location.href = destDecoded.replace(`${protocol}//${hostname}`, "");
	}

	async authentication(params) {
		if (!params) return;
		// do not remove loading state if page will be reloaded
		let willReloadThePage = false;

		try {
			this.loadingEl.classList.remove("hidden");
			this.loadingMessageEl.innerText = "Logging you in";

			if (params.return_url) {
				// Save url to return after login done
				this.setCookie(this.cReturnUrl, params.return_url, 5 * 60 * 1000);
			}

			if (params.code && params.state) {
				// Processing authentication
				const { code, state } = params;
				const userId = await this.exchangeToken({ code, state });
				const returnUrl = this.getCookie(this.cReturnUrl);
				this.updateAccountLinks(userId);

				// redirect user
				if (userId && returnUrl) {
					this.setCookie(this.cReturnUrl, "", -1);
					willReloadThePage = true;
					this.redirect(returnUrl);
				} else if (userId) {
					willReloadThePage = true;
					this.redirect(this.accountUrl);
				}
			} else if (params.doLogin) {
				// Allow do login from any link by appending ?doLogin=true
				// Create login link and redirect user
				const loginLink = await this.generateAuthLink();
				willReloadThePage = true;
				window.location.href = loginLink;
			}
		} finally {
			if (!willReloadThePage) {
				this.loadingEl.classList.add("hidden");
			}
		}
	}

	async initialize() {
		const token = this.getCookie(this.cToken);
		const refreshTk = this.getCookie(this.cRefresh);
		const params = this.checkUrlParams("code", "state", "doLogin", "return_url");

		// Check for current session
		const hasSession = token || refreshTk;

		if (params) {
			await this.authentication(params);
		} else if (!hasSession) {
			// No session available
			this.clearSession();
			this.updateAccountLinks();
		} else if (!token) {
			// Token expired, try refresh
			const userId = await this.exchangeToken({ refreshToken: refreshTk });
			this.updateAccountLinks(userId);
		} else {
			// session active
			const userId = this.getCookie(this.cUserId);
			this.updateAccountLinks(userId);
		}
	}
}

window.addEventListener("DOMContentLoaded", () => {
	if (accountUrl && clientId) {
		const auth = new ShAuth(accountUrl, clientId);
		auth.initialize();
	}
});
